import { inject, injectable } from "inversify";
import type {
  MerchantApprovalData,
  MerchantApprovalDetail,
  MerchantApprovalItem,
} from "../../domain/model/MerchantApprovalModel";
import type {
  IMerchantApprovalRepository,
  IRejectMerchantPayload,
} from "../../domain/repository/IMerchantApprovalRepository";
import type {
  MerchantApprovalDetailDto,
  MerchantApprovalItemDto,
} from "../remote/response/MerchantApprovalResponse";
import { MerchantApprovalApiService } from "../remote/service/MerchantApprovalApiService";

@injectable()
export class MerchantApprovalRepositoryImpl implements IMerchantApprovalRepository {
  @inject(MerchantApprovalApiService)
  private readonly apiService!: MerchantApprovalApiService;

  async fetchApprovals(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<MerchantApprovalData> {
    const response = await this.apiService.fetchApprovals(params);

    return {
      items: response.data.map((dto) => this.mapListItem(dto)),
      paging: {
        currentPage: response.paging.currentPage,
        pageSize: response.paging.pageSize,
        totalRecords: response.paging.totalRecords,
        totalPages: response.paging.totalPages,
        hasNext: response.paging.currentPage < response.paging.totalPages,
        hasPrevious: response.paging.currentPage > 1,
      },
    };
  }

  async fetchDetail(merchantId: string): Promise<MerchantApprovalDetail> {
    const dto = await this.apiService.fetchDetail(merchantId);
    return this.mapDetail(dto);
  }

  async approveMerchant(merchantId: string): Promise<void> {
    await this.apiService.approveMerchant(merchantId);
  }

  async rejectMerchant(
    merchantId: string,
    payload: IRejectMerchantPayload,
  ): Promise<void> {
    await this.apiService.rejectMerchant(merchantId, { reason: payload.reason });
  }

  private mapListItem(dto: MerchantApprovalItemDto): MerchantApprovalItem {
    return {
      merchantId: dto.merchantId,
      merchantName: dto.merchantName,
      brandName: dto.brandName,
      merchantType: dto.merchantType,
      approvalStatus: dto.approvalStatus,
      isActive: dto.isActive,
      phoneNumber: dto.phoneNumber,
      email: dto.email,
      registrationDate: dto.registrationDate,
      createdAt: dto.createdAt,
    };
  }

  private mapDetail(dto: MerchantApprovalDetailDto): MerchantApprovalDetail {
    return {
      merchantId: dto.merchantId,
      merchantName: dto.merchantName,
      brandName: dto.brandName,
      merchantType: dto.merchantType,
      approvalStatus: dto.approvalStatus,
      isActive: dto.isActive,
      createdAt: dto.createdAt,
      businessProfile: dto.businessProfile ? {
        address: dto.businessProfile.address,
        description: dto.businessProfile.description,
        merchantName: dto.businessProfile.merchantName,
        merchantType: dto.businessProfile.merchantType,
        operationalHours: dto.businessProfile.operationalHours,
        logoUrl: dto.businessProfile.logoUrl,
      } : undefined,
      ownerProfile: dto.ownerProfile ? {
        ownerName: dto.ownerProfile.ownerName,
        ownerContact: dto.ownerProfile.ownerContact,
        ownerEmail: dto.ownerProfile.ownerEmail,
        ownerIdPhotoUrl: dto.ownerProfile.ownerIdPhotoUrl,
      } : undefined,
      bankAccount: dto.bankAccount ? {
        bankName: dto.bankAccount.bankName,
        accountHolderName: dto.bankAccount.accountHolderName,
        accountNumber: dto.bankAccount.accountNumber,
      } : undefined,
      legalDocuments: dto.legalDocuments ? {
        npwpNumber: dto.legalDocuments.npwpNumber,
        businessLicenseNumber: dto.legalDocuments.businessLicenseNumber,
        locationPhotoUrl: dto.legalDocuments.locationPhotoUrl,
      } : undefined,
    };
  }
}
